import { memo } from "react";
import { Clock } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { useCountryTranslations } from "../contexts/CountryTranslationsContext";
import { formatMatchTime } from "../lib/dateUtils";
import type { MatchStatus } from "../types";

interface MatchCardProps {
  homeTeam: string;
  awayTeam: string;
  homeFlag: string;
  awayFlag: string;
  matchTime: string;
  status: MatchStatus;
  hasPrediction?: boolean;
  onClick?: () => void;
}

export const MatchCard = memo(function MatchCard({
  homeTeam,
  awayTeam,
  homeFlag,
  awayFlag,
  matchTime,
  status,
  hasPrediction = false,
  onClick,
}: MatchCardProps) {
  const { t, language } = useLanguage();
  const { getCountryName } = useCountryTranslations();

  const translatedHomeTeam = getCountryName(homeTeam);
  const translatedAwayTeam = getCountryName(awayTeam);

  return (
    <div
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`${t(
        "match.vs"
      )} ${translatedHomeTeam} ${translatedAwayTeam}`}
      className={`bg-white rounded-2xl shadow-sm p-5 mb-3 cursor-pointer hover-lift transition-smooth active:scale-98 border ${
        hasPrediction
          ? "border-primary bg-gradient-to-br from-primary-lighter to-white"
          : "border-slate-200"
      } hover:border-primary/20 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="bg-primary-lighter rounded-full p-2">
            <Clock className="w-4 h-4 text-primary" />
          </div>
          <span className="text-slate-600 text-sm font-medium">
            {formatMatchTime(matchTime, language)}
          </span>
        </div>
        {status === "live" && (
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 bg-red-700 rounded-full animate-pulse"></div>
            <span className="px-3 py-1.5 bg-red-700 text-white rounded-full text-xs font-semibold">
              {t("match.live")}
            </span>
          </div>
        )}
        {status === "completed" && (
          <span className="px-3 py-1.5 bg-slate-400 text-white rounded-full text-xs font-semibold">
            {t("match.fullTime")}
          </span>
        )}
      </div>

      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 flex items-center gap-2.5">
          <span className="flex-shrink-0 text-4xl leading-none">
            {homeFlag}
          </span>
          <span className="flex-1 font-semibold text-slate-900 text-left">
            {translatedHomeTeam}
          </span>
        </div>

        <div className="flex-shrink-0 px-4 py-2.5 bg-slate-50 rounded-xl border border-slate-200">
          <span className="text-slate-700 font-semibold text-sm whitespace-nowrap">
            {t("match.vs")}
          </span>
        </div>

        <div className="flex-1 flex items-center gap-2.5 flex-row-reverse">
          <span className="flex-shrink-0 text-4xl leading-none">
            {awayFlag}
          </span>
          <span className="flex-1 font-semibold text-slate-900 text-right">
            {translatedAwayTeam}
          </span>
        </div>
      </div>
    </div>
  );
});
